import { omitDeep } from 'deepdash/standalone';
import { cloneDeep } from 'lodash';

import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { getOptionsForType } from 'modules/queryBuilder/legacyQueryBuilder/components/comparatorDropdown/ComparatorDropdown.utils';
import properties from 'modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/properties';
import { combinatorTextToValueMap } from 'modules/queryBuilder/legacyQueryBuilder/utils/combinators';
import * as comparators from 'modules/queryBuilder/legacyQueryBuilder/utils/comparators';
import { CAMPAIGN_LABEL_FIELD } from 'modules/queryBuilder/legacyQueryBuilder/utils/fields';
import { EXCLUDED_CAMPAIGN_LABEL_PROPERTIES } from 'modules/workflowNodeEditors/utils';
import {
  Combinator,
  DataTypeSearchCombo,
  FieldMatchNodeData,
  SearchCombo,
} from 'modules/workflows/types';
import { getESFieldDisplayName } from 'modules/workflows/utils';
import {
  CAMPAIGN,
  LIST_MEMBERSHIP,
  LIST_MEMBERSHIP_SINGLE,
  MESSAGE_CHANNEL,
  SUBSCRIBED_MESSAGE_TYPES,
  UNSUBSCRIBED_CHANNEL,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST,
  USER_LIST_MEMBERSHIP_SINGLE,
} from 'utils/fieldConstants';

export type Field = {
  fieldType: any;
  field: string;
};

type FormattedField = {
  value: unknown;
  text: string;
  type: string;
};

type FieldProperty = {
  [key: string]: Field;
};

type FormattedFieldProperty = {
  [key: string]: FormattedField;
};

type FieldMappings = {
  [key: string]: FieldProperty;
};

type FormattedFieldMappings = {
  [key: string]: FormattedFieldProperty;
};

type FormatFieldOptions = {
  filterMappings?: (propertyName: string, item: Field) => boolean;
};

/**
 * Formats the fieldMappings (received from fetchEventFieldMappingsRequest) for the queryBuilder component, including  capitalizing the property names and getting the data readable by the dropdowns used.
 */
export const formatFields = (
  fieldMappings: FieldMappings = {},
  /**
   * Function called on each field to determine whether or not it should be included.
   * Functions like a javascript filter -- IE: if `true`, the value is included. If false, the value is excluded.
   */
  { filterMappings = () => true }: FormatFieldOptions = {},
): FormattedFieldMappings => {
  const formatted = Object.keys(fieldMappings).reduce((acc, propertyName) => {
    const fieldMapping = fieldMappings[propertyName] || {};
    const propertyArray = Object.values(fieldMapping).reduce((fieldAcc: FormattedField[], item) => {
      const { field, fieldType } = item;
      const includeMappings = filterMappings(propertyName, item);
      if (!includeMappings) return fieldAcc;

      return [
        ...fieldAcc,
        {
          value: field,
          text: getESFieldDisplayName(field, propertyName),
          type: fieldType,
        },
      ];
    }, [] as FormattedField[]);
    // Adds hardcoded campaign label field to correct properties
    if (!EXCLUDED_CAMPAIGN_LABEL_PROPERTIES.includes(propertyName)) {
      propertyArray.push(CAMPAIGN_LABEL_FIELD);
    }
    return { ...acc, [propertyName]: propertyArray.sort((a, b) => a.text.localeCompare(b.text)) };
  }, {});
  return formatted;
};

/**
 * Get comparable values from a given FieldsMatchEditorNode nodeData object.
 * - Only returns fields that are affected by UI changes
 *
 * @param { Object } data
 * @returns comparable nodeData object
 */
export const getFieldsMatchEditorNodeDataToBeCompared = (
  data:
    | FieldMatchNodeData
    | {
        matchTriggeringEventFields: boolean;
        searchQuery?: SearchCombo;
      },
  keys: string[] = [],
) => {
  // GraphQL keys to be removed in order to be able to do one-to-one comparison against our editor state
  const keysToRemove = ['__typename', ...keys];
  const cleanedData = omitDeep(data, keysToRemove); // remove unwanted keys

  return {
    searchQuery: cleanedData?.searchQuery ?? undefined,
    matchTriggeringEventFields: cleanedData?.matchTriggeringEventFields ?? false, // This is sometimes `null` or `false,` which we want to be interpreted as the same thing
  };
};
// switching over these list-based fields ended up being a common scenario
const performCallbackOnListFields = (field: string, listCb: () => void, campaignCb: () => void) => {
  switch (field) {
    case UNSUBSCRIBED_CHANNELS:
    case LIST_MEMBERSHIP:
    case UNSUBSCRIBED_MESSAGE_TYPES:
    case SUBSCRIBED_MESSAGE_TYPES:
      listCb();
      break;
    case CAMPAIGN:
      campaignCb();
      break;
    default:
      break;
  }
};

/** 
All searchQueries have isClonedData: true so the UI can know.
All lists, campaigns, message channels, messages types, etc have no value so the user can't save a non-existent value
 */
export const prepareClonedData = (parsedClonedData: SearchCombo) => {
  const copyOfParsedClonedData = cloneDeep(parsedClonedData);
  // cloneDeep'd so we can just do this imperatively and still return a brand new object
  (copyOfParsedClonedData?.searchQueries as SearchCombo[])?.forEach((group) => {
    (group?.searchQueries as DataTypeSearchCombo[])?.forEach((searchQuery) => {
      const { searchCombo } = searchQuery;
      (searchCombo?.searchQueries as Query[])?.forEach((query) => {
        // cloneDeep'd, can avoid immutability approaches

        query.isClonedData = true;

        const listCb = () => {
          // cloneDeep'd, can avoid immutability approaches

          query.value = '';
        };
        const campaignCb = () => {
          // cloneDeep'd, can avoid immutability approaches

          query.values = [];
        };
        performCallbackOnListFields(query.field, listCb, campaignCb);
      });
    });
  });

  return copyOfParsedClonedData;
};

type ListOrCampaign = {
  field: string;
  value?: string | null;
  values?: string[] | null;
  location: string;
};
type ListsAndCampaigns = ListOrCampaign[];
const buildLocation = (combinator: Combinator, query: Query) => {
  const property = properties.find((p) => p.value === query.dataType)?.text;
  const combinatorText = combinatorTextToValueMap[combinator];

  const comparatorOptionsForType = getOptionsForType(query.dataType, query.field, comparators);
  const comparatorText = comparatorOptionsForType.find(
    (option) => option.value === query.comparatorType,
  )?.text;
  return `From "${combinatorText} ${property}, where ${getESFieldDisplayName(
    query.field,
    query.dataType,
  )} ${comparatorText}..."`;
};
export const getListsAndCampaigns = (parsedNodeData: SearchCombo) => {
  const listsAndCampaigns = [] as ListsAndCampaigns;
  // ideal location: ['from Any', 'Email Click,', 'where Campaign', 'equals','...']
  (parsedNodeData?.searchQueries as SearchCombo[])?.forEach((group) => {
    const { combinator } = group;
    (group?.searchQueries as DataTypeSearchCombo[])?.forEach((searchQuery) => {
      const { searchCombo } = searchQuery;
      (searchCombo?.searchQueries as Query[])?.forEach((query) => {
        const location = buildLocation(combinator, query);
        const listCb = () =>
          listsAndCampaigns.push({
            field: query.field,
            value: query.value?.toString(),
            location,
          });
        const campaignCb = () =>
          listsAndCampaigns.push({
            field: query.field,
            values: query.values,
            location,
          });

        performCallbackOnListFields(query?.field, listCb, campaignCb);
      });
    });
  });
  return listsAndCampaigns;
};

const MESSAGE_TYPE_FIELD = 'messageTypeField';
const CHANNEL_TYPE_FIELD = 'channelTypeField';
const LIST_TYPE_FIELD = 'listTypeField';
const CAMPAIGN_TYPE_FIELD = 'campaignTypeField';
const FIELD_MAP = {
  [CAMPAIGN]: CAMPAIGN_TYPE_FIELD,
  [UNSUBSCRIBED_MESSAGE_TYPES]: MESSAGE_TYPE_FIELD,
  [SUBSCRIBED_MESSAGE_TYPES]: MESSAGE_TYPE_FIELD,
  [UNSUBSCRIBED_CHANNEL]: CHANNEL_TYPE_FIELD,
  [UNSUBSCRIBED_CHANNELS]: CHANNEL_TYPE_FIELD,
  [MESSAGE_CHANNEL]: CHANNEL_TYPE_FIELD,
  [USER_LIST_MEMBERSHIP_SINGLE]: LIST_TYPE_FIELD,
  [LIST_MEMBERSHIP_SINGLE]: LIST_TYPE_FIELD,
  [LIST_MEMBERSHIP]: LIST_TYPE_FIELD,
  // @ts-expect-error there are multiple field names with the same value, causing a ts type error
  [USER_LIST]: LIST_TYPE_FIELD,
} as Record<string, string>;
const FIELD_MESSAGE_MAP = {
  [CAMPAIGN_TYPE_FIELD]: 'campaigns',
  [CHANNEL_TYPE_FIELD]: 'channels',
  [LIST_TYPE_FIELD]: 'lists',
  [MESSAGE_TYPE_FIELD]: 'message types',
} as Record<string, string>;

type ValidationMessageObject = {
  location: string;
  items: string[];
};
type Validations = {
  validationMessageHeader: string;
  validationMessages: ValidationMessageObject[];
};
export const formValidationMessagesForLists = (parsedClonedData: SearchCombo): Validations[] => {
  const lists = getListsAndCampaigns(parsedClonedData);
  const validations = {} as Record<string, Validations>;
  lists.forEach((list) => {
    const { field, value, values, location } = list;
    const fieldMapping = FIELD_MAP[field];

    if (fieldMapping) {
      const messages = values || ([value] as string[]);
      if (!validations[fieldMapping]) {
        const validationMessage = {
          validationMessageHeader: `Some referenced ${FIELD_MESSAGE_MAP[fieldMapping]} are missing in the current project`,
          validationMessages: [],
        };
        validations[fieldMapping] = validationMessage;
      }
      const validationMessageObject = { location, items: messages };
      validations[fieldMapping].validationMessages.push(validationMessageObject);
    }
  });
  return Object.values(validations);
};
